import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import QRCode from 'qrcode.react';
import { FiCheck, FiX, FiLock, FiMail, FiSmartphone, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import '../styles/MFASettings.css';

// Import correct functions for AWS Amplify v6
import { 
  getCurrentUser,
  setUpTOTP,
  signOut,
  verifyTOTPSetup,
  fetchMFAPreference,
  updateMFAPreference,
  fetchUserAttributes
} from 'aws-amplify/auth';

// Debug control - use i.e. DEBUG && console.log("whatever") and set below const true
const DEBUG = process.env.REACT_APP_DEBUG == 'enabled';

const MFASettings = () => {
  // State for MFA settings
  const [mfaType, setMfaType] = useState(['NONE']);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [userAttributes, setUserAttributes] = useState({});
  const [mfaPreferred, setMfaPreferred] = useState('NONE');

  // Fetch current MFA settings on component mount
  useEffect(() => {
    fetchMFASettings();
  }, []);

  const fetchMFASettings = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const user = await getCurrentUser();
      DEBUG && console.log('Current user:', user);
      
      // Get user attributes using fetchUserAttributes (v6 approach)
      const attributes = await fetchUserAttributes();
      DEBUG && console.log('User attributes:', attributes);
      setUserAttributes(attributes);
      
      // Store email for later use
      const email = attributes.email || '';
      setUserEmail(email);
      
      // Check if email is verified
      const isEmailVerified = attributes.email_verified === 'true' || attributes.email_verified === true;
      DEBUG && console.log('Email verified:', isEmailVerified);
      setEmailVerified(isEmailVerified);
      
      // Get preferred MFA method
      const { preferred, enabled } = await fetchMFAPreference();
      DEBUG && console.log('MFA preferences:', { preferred, enabled });
      
      // Set the MFA type based on the preferred method
      if (enabled?.includes('TOTP') && !enabled?.includes('EMAIL')) {
        DEBUG && console.log("Enabled MFA is detected to be TOTP");
        setMfaType(['TOTP']);
      } else if (!enabled?.includes('TOTP') && enabled?.includes('EMAIL')) {
        DEBUG && console.log("Enabled MFA is detected to be EMAIL");
        setMfaType(['EMAIL']);
      } else if (enabled?.includes('TOTP') && enabled?.includes('EMAIL')) {
        DEBUG && console.log("Enabled MFA is detected to be TOTP and EMAIL");
        setMfaType(['TOTP','EMAIL']);
      } 
        else {
          DEBUG && console.log("Enabled MFA is detected to be NONE - "+enabled);
        setMfaType(['NONE']);
      }

      if (preferred == 'EMAIL') {
        setMfaPreferred('EMAIL');
      } else if (preferred == 'TOTP') {
        setMfaPreferred('TOTP');
      } else {
        setMfaPreferred('NONE');
      }

      DEBUG && console.log("mfaType as stored: "+mfaType);
      DEBUG && console.log("mfaPreferred as stored: "+mfaPreferred);
      
    } catch (error) {
      if (error == 'NotAuthorizedException: Access Token has been revoked')
      {
        signOut();
      } else
      {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load MFA settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const setupTOTPMfa = async () => {
    try {
      setIsSettingUp(true);
      
      // Use setupTOTPMFA (v6 approach)
      const totpResponse = await setUpTOTP();
      DEBUG && console.log('TOTP Setup Response:', totpResponse);
      
      // Get the secret code (correct property name in v6)
      const secretCode = totpResponse?.sharedSecret;
      
      if (!secretCode) {
        throw new Error('Could not get TOTP secret code');
      }
      
      // Generate QR code with the correct user information
      const accountName = userEmail || (await getCurrentUser()).username;
      const issuer = 'IGEL ID';
      const qrUrl = `otpauth://totp/${issuer}:${accountName}?secret=${secretCode}&issuer=${issuer}`;
      
      setQrCode(qrUrl);
      setSecret(secretCode);
    } catch (error) {
      console.error('Error setting up TOTP:', error);
      toast.error('Failed to set up TOTP: ' + error.message);
      setIsSettingUp(false);
    }
  };

  const verifyTOTP = async () => {
    try {
      // Verify the TOTP setup with the verification code
      await verifyTOTPSetup({
        code: verificationCode  // Correct parameter name in v6 is 'code'
      });
      
      // Set TOTP as the preferred MFA method
      await updateMFAPreference({
        totp: 'ENABLED'  
      });
      
      // Reset state
      setVerificationCode('');
      setQrCode('');
      setSecret('');
      setIsSettingUp(false);
      
      // Update MFA type
      setMfaType('TOTP');
      
      toast.success('Authenticator app MFA enabled successfully');
      
      // Refresh MFA settings
      await fetchMFASettings();
    } catch (error) {
      console.error('Error verifying TOTP:', error);
      toast.error('Failed to verify code: ' + error.message);
    }
  };

  const handleEmailMFA = async () => {
    try {
      setLoading(true);
      
      if (!emailVerified) {
        toast.error('Your email must be verified to enable Email MFA');
        return;
      }
      
      // Check current MFA type
      if (mfaType == 'EMAIL') {
        DEBUG && console.log('Detected MFA type === EMAIL');
        // If Email MFA is already enabled, disable it by setting to DISABLED
        await updateMFAPreference({
          email: 'DISABLED'  
        });

        
        setMfaType('NONE');
        toast.success('Email MFA has been disabled');
      } else  if (mfaType.includes('EMAIL') && mfaType.includes('TOTP'))
        {
          DEBUG && console.log('Detected MFA type both');
        // If Email MFA is already enabled, disable it by setting to DISABLED
        await updateMFAPreference({
          email: 'DISABLED',  
          totp: 'ENABLED'
        });

        
        setMfaType('TOTP');
        toast.success('Email MFA has been disabled, authenticator app is now preferred');
        }
        
        else {
          DEBUG && console.log('Didnt detect');
        // Enable Email MFA
        await updateMFAPreference({
          email: 'ENABLED'  
        });
        
        setMfaType('EMAIL');
        toast.success('Email MFA has been enabled');
      }
      
      // Refresh MFA settings to confirm changes
      await fetchMFASettings();
    } catch (error) {
      console.error('Error with Email MFA:', error);
      toast.error('There was an issue with Email MFA: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const disableTOTPMfa = async () => {
    try{
      setLoading(true);
      if (mfaType == 'TOTP')
      {
        // If only TOTP MFA is enabled, only modify TOTP preference
        await updateMFAPreference({
          totp: 'DISABLED'
        });
        setMfaType('NONE');
        toast.success('TOTP MFA has been disabled');
      } else if (mfaType.includes('EMAIL') && mfaType.includes('TOTP'))
      {
        // If both MFA methods are enabled make sure preferred MFA changes to EMAIL with TOTP disabled
        await updateMFAPreference({
          totp: 'DISABLED',
          email: 'ENABLED'
        });
        setMfaType('EMAIL');
        toast.success('TOTP MFA has been disabled, e-mail verification is now preferred');
      } else
      {
        throw('Unexpected MFA combination. Please contact IGEL Customer Support.');
      }
    }
    catch (error) {
      console.error('Error disabling MFA:', error);
      toast.error('There was an issue disabling TOTP MFA: ' + error.message);
    }
    finally {
      setLoading(false);
    }
  }

  const disableAllMFA = async () => {
    try {
      setLoading(true);
      
      // Disable MFA using v6 method
      await updateMFAPreference({
        email: 'DISABLED',
        totp: 'DISABLED'  
      });
      
      setMfaType('NONE');
      toast.success('MFA has been disabled');
      
      // Refresh MFA settings
      await fetchMFASettings();
    } catch (error) {
      console.error('Error disabling MFA:', error);
      toast.error('There was an issue disabling MFA: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelSetup = () => {
    setIsSettingUp(false);
    setQrCode('');
    setSecret('');
    setVerificationCode('');
  };

  // Display loading state
  if (loading && !isSettingUp) {
    return (
      <div className="mfa-settings">
        <h2>Multi-Factor Authentication</h2>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading MFA settings...</p>
        </div>
      </div>
    );
  }

  // Render TOTP setup
  const renderTOTPSetup = () => {
    return (
      <div className="totp-setup">
        <h3>Set up Authenticator App</h3>
        <p>Scan this QR code with your authenticator app (like Google Authenticator, Microsoft Authenticator, or Authy).</p>
        
        <div className="qr-container">
          {qrCode && <QRCode value={qrCode} size={200} />}
        </div>
        
        <div className="secret-key">
          <p>If you can't scan the QR code, enter this secret key manually:</p>
          <div className="secret-code">{secret}</div>
        </div>
        
        <div className="verification-section">
          <p>Enter the 6-digit verification code from your authenticator app:</p>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
            placeholder="Enter 6-digit code"
            maxLength={6}
            pattern="[0-9]*"
            inputMode="numeric"
          />
          
          <div className="action-buttons">
            <button 
              onClick={cancelSetup} 
              className="cancel-button"
              aria-label="Cancel setup"
            >
              <FiX /> Cancel
            </button>
            <button 
              onClick={verifyTOTP} 
              className="verify-button"
              disabled={verificationCode.length !== 6}
              aria-label="Verify code"
            >
              <FiCheck /> Verify Code
            </button>
          </div>
        </div>
      </div>
    );
  };

  const isTOTPEnabled = mfaType?.includes('TOTP');
  const isEmailMFAEnabled = mfaType?.includes('EMAIL');
  const isTOTPPreferred = mfaPreferred == ('TOTP');
  const isEmailPreferred = mfaPreferred == ('EMAIL');

  return (
    <div className="mfa-settings">
      <h2>Multi-Factor Authentication</h2>
      
      {isSettingUp ? (
        renderTOTPSetup()
      ) : (
        <div className="mfa-status">
          <div className="current-status">
            <h3>Current MFA Status</h3>
            
            <div className="mfa-method-list">
              <div className={`status-indicator ${isTOTPEnabled ? 'active' : 'inactive'}`}>
                <FiSmartphone />
                <span>Authenticator App: {isTOTPEnabled ? 'Enabled' : 'Disabled'}</span>
                {isTOTPPreferred && <span className="primary-badge">Preferred</span>}
              </div>
              
              <div className={`status-indicator ${isEmailMFAEnabled ? 'active' : 'inactive'}`}>
                <FiMail />
                <span>Email Verification: {isEmailMFAEnabled ? 'Enabled' : 'Disabled'}</span>
                {isEmailPreferred && <span className="primary-badge">Preferred</span>}
              </div>
            </div>
          </div>
          
          <div className="mfa-options">
            <h3>MFA Options</h3>
            
            <div className="option authenticator-option">
              <h4><FiSmartphone className="option-icon" /> Authenticator App</h4>
              
              
              {!isTOTPEnabled ? (
                <>
                <p>Use an authenticator app like Google Authenticator, Microsoft Authenticator, or Authy to generate verification codes.</p>
                <button 
                  onClick={setupTOTPMfa} 
                  className="setup-button"
                  aria-label="Set up authenticator app"
                >
                  <FiToggleLeft className="toggle-icon" /> Set up Authenticator App
                </button>
                </>
              ) : (
                <>
                <p>Authenticator App is currently configured. You can use it for verification when signing in.</p>
                <button 
                  onClick={disableTOTPMfa} 
                  className="disable-button"
                  aria-label="Disable"
                >
                  <FiToggleRight className="toggle-icon" /> Disable
                </button>
                </>
              )}
            </div>
            
            <div className={`option email-option ${!emailVerified ? 'mfa-info' : ''}`}>
              <h4><FiMail className="option-icon" /> Email MFA</h4>
              {!emailVerified ? (
                <>
                  <p>Your email needs to be verified before you can enable Email MFA.</p>
                  <div className="email-verify-info">
                    <p>Current email: {userEmail || 'No email set'}</p>
                  </div>
                </>
              ) : isEmailMFAEnabled ? (
                <>
                  <p>Email MFA is currently enabled. You can receive verification codes via email when signing in.</p>
                  <button 
                    onClick={handleEmailMFA} 
                    className="disable-button"
                    aria-label="Disable Email MFA"
                  >
                    <FiToggleRight className="toggle-icon" /> Disable
                  </button>
                </>
              ) : (
                <>
                  <p>Receive verification codes via email when you sign in to your account.</p>
                  <button 
                    onClick={handleEmailMFA} 
                    className="setup-button email-button"
                    aria-label="Enable Email MFA"
                  >
                    <FiToggleLeft className="toggle-icon" /> Enable Email MFA
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFASettings;